import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Helmet } from 'react-helmet-async';
import { 
  Infinity, 
  Globe, 
  ShieldCheck, 
  LineChart, 
  Receipt, 
  Package, 
  Users, 
  Wallet, 
  Calendar, 
  Star, 
  HeartHandshake, 
  BellRing, 
  UserCircle, 
  Monitor, 
  Bot,
  PieChart,
  BellElectric
} from 'lucide-react';

const Features = () => {
  const [activeTab, setActiveTab] = useState('crm');

  const categories = [
    { id: 'crm', name: 'Müşteri İlişkileri & Randevu' },
    { id: 'finance', name: 'Finans ve Kasa Çözümleri' },
    { id: 'stock', name: 'Stok Yönetimi Çözümleri' },
    { id: 'staff', name: 'Personel Yönetimi' },
  ];

  const featuresData = {
    crm: [
      { icon: <Infinity className="text-primary-500" size={32} />, title: 'Sınırsız Randevu Havuzu', desc: 'Sınır ve kota yok. İşletmenize dilediğiniz kadar randevu kaydedin.' },
      { icon: <Calendar className="text-rose-500" size={32} />, title: 'Akıllı Randevu Ajandası', desc: 'Kolay kullanımlı, takvim görünümlü modern ajanda ile saatleri hatasız yönetin.' },
      { icon: <HeartHandshake className="text-pink-500" size={32} />, title: 'Müşteri Kartları (CRM)', desc: 'Müşterilerinizin tüm geçmiş hizmetlerini, notlarını ve veresiye harcamalarını tek ekranda görün.' },
      { icon: <BellRing className="text-red-500" size={32} />, title: 'Otomatik Hatırlatıcı Servisi', desc: 'Yaklaşan randevular için otomatik anlık bildirim (Push) göndererek unutulmaların ve kayıpların önüne geçin.' },
      { icon: <UserCircle className="text-fuchsia-500" size={32} />, title: 'Müşterilerin Kendi Hesabı', desc: 'Müşterileriniz alan adınıza girip kendi hesaplarından randevularını kolayca oluşturabilir.' },
      { icon: <Globe className="text-blue-500" size={32} />, title: 'Instagram Bio Sitesi', desc: 'Instagram profilinize ekleyebileceğiniz özel tasarımlı subdomain web siteniz tamamen ücretsiz.' }
    ],
    finance: [
      { icon: <LineChart className="text-indigo-500" size={32} />, title: 'Kapsamlı Finans Takibi', desc: 'İşletmenizin günlük, aylık gelir/gider detaylarını anlık olarak takip edin ve grafiğe dökün.' },
      { icon: <Receipt className="text-orange-500" size={32} />, title: 'Adisyon ve Hızlı Satış', desc: 'Hizmetler veya ürünler için saniyeler içinde adisyon oluşturun, kasadan nakit/kart ödemesini alın.' },
      { icon: <Monitor className="text-sky-500" size={32} />, title: 'Bağımsız Kasa Hesabı', desc: 'Kasiyer girişleri için sadece adisyonları ve tahsilat ekranlarını gören izole bir modül.' },
      { icon: <PieChart className="text-purple-500" size={32} />, title: 'Gelir ve Gider Analizi', desc: 'Hangi hizmetten ne kadar kazanılmış, giderler nerelere yapılmış hepsini detaylı analiz edin.' }
    ],
    stock: [
      { icon: <Package className="text-amber-500" size={32} />, title: 'Stok ve Satış Geçmişi', desc: 'Ürün giriş ve çıkışlarını tarih tarih listeleyerek geçmişe dönük stok hareketlerini izleyin.' },
      { icon: <LineChart className="text-teal-500" size={32} />, title: 'Stok ve Satış Analizleri', desc: 'En çok satan ürünleri belirleyerek yatırım planınızı yapın, kârlı ürünleri öne çıkarın.' },
      { icon: <BellElectric className="text-red-500" size={32} />, title: 'Kritik Stok Uyarısı', desc: 'Ürün stokları belirlediğiniz kritik seviyenin altına düştüğünde sistem sizi otomatik uyarsın.' }
    ],
    staff: [
      { icon: <Users className="text-cyan-500" size={32} />, title: 'Detaylı Personel Analizleri', desc: 'Hangi personelin ne kadar ciro yaptığını ve kaç randevu aldığını tek tıkla görün.' },
      { icon: <Wallet className="text-teal-500" size={32} />, title: 'Prim ve Maaş Sistemi', desc: 'Personel prim oranlarını belirleyin, sistem ay sonu hakediş ve maaşları tamamen otomatik hesaplasın.' },
      { icon: <Star className="text-yellow-500" size={32} />, title: 'Personel Değerlendirme', desc: 'Müşterilerinizin personelinize verdiği 5 yıldız üzerinden puanlar ve yorumlarla kaliteyi artırın.' },
      { icon: <ShieldCheck className="text-emerald-500" size={32} />, title: 'Yetki Bazlı Kısıtlamalar', desc: 'Personelleriniz sadece kendi ajandalarını görsün, işletmenin finansal verilerine erişemesin.' }
    ]
  };

  const containerVariants = {
    hidden: { opacity: 0 },
    show: {
      opacity: 1,
      transition: { staggerChildren: 0.15 }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 40, scale: 0.95 },
    show: { 
      opacity: 1, 
      y: 0, 
      scale: 1,
      transition: { type: "spring", stiffness: 100, damping: 20 }
    }
  };

  return (
    <>
      <Helmet>
        <title>Sistem Özellikleri ve Çözümler | RandevUPP 2.0</title>
        <meta name="description" content="RandevUPP 2.0 işletme yönetim sisteminin eşsiz özelliklerini inceleyin. Finans çözümleri, stok yönetimi, müşteri ilişkileri ve daha fazlası." />
        <meta property="og:title" content="Sistem Özellikleri ve Çözümler | RandevUPP 2.0" />
        <meta property="og:description" content="RandevUPP 2.0 ile Finans, Stok, Personel ve Randevu yönetimi çok daha kolay." />
      </Helmet>

      <div className="py-12 md:py-24 px-4">
        <div className="text-center mb-12 md:mb-16">
          <motion.h1 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-3xl md:text-5xl font-bold tracking-tight text-gray-900 dark:text-white mb-6"
          >
            Tüm Çözümler Tek Ekranda
          </motion.h1>
          <motion.p 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="text-base md:text-lg text-gray-600 dark:text-gray-400 max-w-3xl mx-auto"
          >
            RandevUPP 2.0, salonunuzun ve stüdyonuzun ihtiyaç duyabileceği tüm işlemleri en sade ve modern arayüzle size sunar. İhtiyacınız olan kategoriyi seçin ve keşfedin.
          </motion.p>
        </div>

        {/* Tabs */}
        <div className="max-w-5xl mx-auto mb-12">
          <div className="flex flex-wrap items-center justify-center gap-2 md:gap-4 p-2 glass-panel rounded-3xl">
            {categories.map((category) => (
              <button
                key={category.id}
                onClick={() => setActiveTab(category.id)}
                className={`px-4 py-3 md:px-6 md:py-3 rounded-2xl text-sm md:text-base font-semibold transition-all flex-grow sm:flex-grow-0 ${
                  activeTab === category.id
                    ? 'bg-primary-500 text-white shadow-lg shadow-primary-500/30'
                    : 'text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-800'
                }`}
              >
                {category.name}
              </button>
            ))}
          </div>
        </div>

        {/* Tab Content */}
        <div className="max-w-7xl mx-auto min-h-[500px]">
          <AnimatePresence mode="wait">
            <motion.div
              key={activeTab}
              variants={containerVariants}
              initial="hidden"
              animate="show"
              exit={{ opacity: 0, y: -20, transition: { duration: 0.2 } }}
              className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6"
            >
              {featuresData[activeTab].map((feature, idx) => (
                <motion.div 
                  key={idx}
                  variants={itemVariants}
                  className="glass-panel p-6 rounded-3xl relative overflow-hidden group hover:shadow-2xl transition-all duration-300 hover:-translate-y-1"
                >
                  <div className="w-14 h-14 rounded-2xl bg-gray-50 dark:bg-gray-800/80 flex items-center justify-center mb-6 group-hover:scale-110 transition-transform shadow-inner">
                    {feature.icon}
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-3">
                    {feature.title}
                  </h3>
                  <p className="text-gray-600 dark:text-gray-400 text-sm leading-relaxed">
                    {feature.desc}
                  </p>
                </motion.div>
              ))}
            </motion.div>
          </AnimatePresence>
        </div>
        
        {/* Coming Soon AI feature attached to bottom */}
        <div className="max-w-4xl mx-auto mt-16 md:mt-24">
          <motion.div 
            initial={{ opacity: 0, scale: 0.95 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            className="p-8 rounded-3xl bg-gradient-to-r from-gray-900 to-gray-800 dark:from-white dark:to-gray-200 text-white dark:text-gray-900 shadow-2xl relative overflow-hidden"
          >
             <div className="absolute top-4 right-4 px-3 py-1 bg-primary-500 text-white text-xs font-bold rounded-full animate-pulse">
                Sürüm 2.3'te Yakında
             </div>
             <div className="flex flex-col md:flex-row items-center gap-6">
                <div className="w-16 h-16 rounded-full bg-white/10 dark:bg-black/5 flex items-center justify-center shrink-0">
                  <Bot size={36} className="text-gray-100 dark:text-gray-900" />
                </div>
                <div>
                  <h3 className="text-2xl font-bold mb-2">Yapay Zeka Destekli İşletme Asistanı</h3>
                  <p className="text-gray-300 dark:text-gray-700 text-sm md:text-base leading-relaxed">
                    İşletmenizin geleceğini yapay zeka öngörüsüyle planlayın. Randevu yoğunluğunu tahmin eden ve size detaylı stratejiler sunan sanal asistanımız çok yakında sizlerle.
                  </p>
                </div>
             </div>
          </motion.div>
        </div>

      </div>
    </>
  );
};

export default Features;
